import { ContentType } from "../Common/ContentType";
import { ISourcedUserFact, IUserFact, UserFactSource } from "../Common/Models";
import { getSetting, getSamplingRate } from "../Common/Settings";
import * as Configuration from "../Configuration/Configuration";
import { EventIds } from "../Logging/Constants";
import * as Logging from "../Logging/Logger";
import { saveToCache } from "../Utils/Caching";
import { sendGetRequest } from "../Utils/Transport";
import { createTimeoutPromise, getErrorMessage } from "../Utils/Utils";
import { UserFactLocalStorageKey, mergeLocalUserFacts } from "./CachedUserFacts";
import { AppUsageUserFactType } from "./Constants";

export function createStringOfFactNames(factNames: string[]): string {
	if (!factNames)
		return undefined;

	return factNames.join(", ");
}

export function getUserFactNames(userFacts: IUserFact[]): string[] {
	return userFacts?.map(uf => uf.userFactName);
}

function createTelemetryData(requestStartTimestamp: number, response: Response, count = -1, factNames: string[] = undefined, samplingRate = -1) {
	const { headers, status, statusText } = response || {} as Response;
	const serverSessionId = headers && headers.get("X-ClientSessionId");
	const serverCorrelationId = headers && headers.get("X-CorrelationId");

	return {
		CorrelationId: serverCorrelationId || undefined,
		Count: count,
		HttpStatusCode: status || undefined,
		HttpStatusText: statusText || undefined,
		FactNames: createStringOfFactNames(factNames),
		SamplingRate: samplingRate,
		SessionId: serverSessionId,
		TimeMilliseconds: +new Date() - requestStartTimestamp,
	};
}

export async function fetchUserFacts(authToken: string): Promise<IUserFact[]> {
	const requestStartTime = +new Date();
	let userFacts = null;

	// make service call and save the response to local cache
	try {
		const serviceUrl = getSetting("userFactsUrl") as string;
		const samplingRate = getSamplingRate();
		const response: Response = await sendGetRequest(serviceUrl, authToken, Configuration.get().getSessionId());
		if (response.status === 200 || response.status === 204) {
			const userFactsResponse: IUserFact[] = await response.json();
			userFacts = updateAndSaveUserFacts(userFactsResponse);

			if (!userFacts || userFacts.length === 0) {
				Logging.getLogger().event(EventIds.UserFactsCommonErrorNoFactsReturned, createTelemetryData(requestStartTime, response));
			}
			else {
				Logging.getLogger().event(EventIds.UserFactsCommonInfo, createTelemetryData(requestStartTime, response, 
					userFacts.length, getUserFactNames(userFacts), samplingRate), samplingRate);
			}
		} else {
			Logging.getLogger().event(EventIds.UserFactsCommonError, createTelemetryData(requestStartTime, response));
		}
	} catch (error) {
		Logging.getLogger().event(EventIds.UserFactsCommonError, { ...createTelemetryData(requestStartTime, null),
			error: getErrorMessage(error) });
	}

	return userFacts;
}

export function fetchUserFactsWithTimeout(authToken: string): Promise<IUserFact[]> {
	const userFactsPromise = fetchUserFacts(authToken);
	const requestTimeout = getSetting<number>("userFactsRequestTimeOut");

	return createTimeoutPromise(requestTimeout, userFactsPromise);
}

function updateAndSaveUserFacts(userFactsResponse: IUserFact[]): IUserFact[] {
	if (!userFactsResponse || userFactsResponse.length == 0) {
		return null;
	}

	const normalizedUserFacts = addClientIngestionTimeAndNormalizeKeys(userFactsResponse);
	const userFacts = mergeLocalUserFacts(normalizedUserFacts);
	const cacheExpiration = getSetting<number>("userFactsCacheExpiration");
	saveToCache(ContentType.userFacts, userFacts, cacheExpiration, UserFactLocalStorageKey);
	return userFacts;
}

export function addClientIngestionTimeAndNormalizeKeys(tulipsResponseFacts: IUserFact[]): IUserFact[] {
	const currentDateTime = new Date(Date.now()).toISOString();

	return tulipsResponseFacts.map((responseFact) => {
		const fact = normalizeKeys(responseFact);
		fact.clientIngestionDateTime = currentDateTime;
		return fact;
	});
}

/**
 * Iterate through the current list of user facts to update the source date time of given fact
 * @param userFactName the user fact to be updated
 * @param newSourceDateTime the new source date time
 * @param userFacts the list of user facts
 */
export function addOrUpdateAppUsageUserFact(userFactName: string, newSourceDateTime: string, userFacts: IUserFact[]): IUserFact[] {
	if (!userFacts) {
		userFacts = [];
	}

	const fact: ISourcedUserFact = userFacts.find(uf => uf.userFactName === userFactName);
	if (fact) { 
		fact.sourceDateTime = newSourceDateTime;
		fact.userFactValue = newSourceDateTime;
		fact.storageDateTime = newSourceDateTime;
		fact.source = UserFactSource.Local;
	} else { 
		// if fact did not already exist in cache, add it
		const newFact: ISourcedUserFact = {
			userFactType: AppUsageUserFactType,
			userFactName: userFactName,
			userFactValue: newSourceDateTime,
			sourceDateTime: newSourceDateTime,
			storageDateTime: newSourceDateTime,
			clientIngestionDateTime: "",
			source: UserFactSource.Local
		};

		userFacts.push(newFact);
	}

	return userFacts;
}

/**
 * This method normalizes the keys of the user fact object retrieved from the TULIPS service.
 * Specifically it converts the first letter of each key in the object to lower case.
 * For example the key "UserFactType" returned from the service will be converted to "userFactType".
 * @param userFact the user fact object
 */
function normalizeKeys(userFact: IUserFact) {
	const normalizedUserFact = {} as IUserFact;
	for (const key of Object.keys(userFact)) {
		const normalizedKey = (key.charAt(0).toLowerCase() + key.substring(1)) as keyof IUserFact;
		normalizedUserFact[normalizedKey] = (userFact as any)[key] as (string & Date);
	}

	return normalizedUserFact;
}
